<template>
  <div class="formester-radio-options">
    <div ref="listContainer">
      <div
        v-for="(option, index) in internalRanking"
        :key="option?.id"
        :ref="(el) => (itemRefs[index] = el)"
        class="formester-control radio-option my-2"
        :class="{ 'is-dragging': draggedIndex === index }"
        :style="getItemStyle(index)"
      >
        <label class="d-flex align-items-center altenate-radio-label">
          <Dropdown
            :model-value="index + 1"
            :items="dropdownOptions"
            class="ranking-dropdown"
            @update:model-value="onDropdownChange(index, $event)"
          />
          <div
            class="d-flex justify-content-between align-items-center ms-1 w-100"
          >
            <div>
              <input
                :id="option?.id"
                :name="`${item.label}-${item.id}`"
                type="radio"
                class="custom-radio formester-input"
                :disabled="isEditingDisabled || editing"
              />
              <span class="option-label" :style="optionStyles">
                {{ option?.label }}
              </span>
            </div>
            <div
              class="drag-handle"
              @mousedown="startDrag($event, index)"
              @touchstart="startDrag($event, index)"
            >
              <DraggableIcon />
            </div>
          </div>
        </label>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import Dropdown from "@/pages/dropdown.vue";
import DraggableIcon from "@/components/builder/components/draggable-icon.vue";

const props = defineProps({
  ranking: {
    type: Array,
    required: true,
  },
  item: {
    type: Object,
    required: true,
  },
  editing: {
    type: Boolean,
    default: false,
  },
  dropdownOptions: {
    type: Array,
    required: true,
  },
  optionStyles: {
    type: Object,
    default: () => ({}),
  },
  isEditingDisabled: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(["update:ranking"]);

const internalRanking = ref([...props.ranking]);
const listContainer = ref(null);
const itemRefs = ref([]);
const itemPositions = ref([]);
const draggedIndex = ref(null);
const dragStartY = ref(0);
const dragCurrentY = ref(0);
const itemHeight = ref(0);
const dragOffset = ref(0);

onMounted(() => {
  updateItemPositions();
});

onBeforeUnmount(() => {
  document.removeEventListener("mousemove", handleMove);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleMove);
  document.removeEventListener("touchend", stopDrag);
});

watch(
  () => props.ranking,
  (newVal) => {
    internalRanking.value = [...newVal];
  },
  { deep: true },
);

function updateItemPositions() {
  if (!itemRefs.value.length) return;

  itemPositions.value = itemRefs.value.map((el) => {
    if (!el) return null;
    const rect = el.getBoundingClientRect();

    return {
      top: rect.top,
      bottom: rect.bottom,
      height: rect.height,
    };
  });

  if (itemRefs.value[0]) {
    itemHeight.value = itemRefs.value[0].offsetHeight;
  }
}

function startDrag(event, index) {
  event.preventDefault();
  const y =
    event.type === "touchstart" ? event.touches[0].clientY : event.clientY;

  draggedIndex.value = index;
  dragStartY.value = y;
  dragCurrentY.value = y;

  // Calculate drag offset within the item
  const itemRect = itemRefs.value[index].getBoundingClientRect();
  dragOffset.value = y - itemRect.top;
  updateItemPositions();

  if (event.type === "touchstart") {
    document.addEventListener("touchmove", handleMove, { passive: false });
    document.addEventListener("touchend", stopDrag);
  } else {
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", stopDrag);
  }
}

function handleMove(event) {
  event.preventDefault();
  if (draggedIndex.value === null) return;

  const y =
    event.type === "touchmove" ? event.touches[0].clientY : event.clientY;
  dragCurrentY.value = y;

  // Calculate the center position of the dragged item
  const draggedCenter = y - dragOffset.value + itemHeight.value / 2;

  // Find the new index based on position
  let newIndex = 0;
  for (let i = 0; i < itemPositions.value.length; i++) {
    const pos = itemPositions.value[i];
    if (!pos) continue;

    if (draggedCenter > pos.top) {
      newIndex = i;
    }
  }

  // Update order if index changed
  if (newIndex !== draggedIndex.value) {
    const newRanking = [...internalRanking.value];
    const [removed] = newRanking.splice(draggedIndex.value, 1);
    newRanking.splice(newIndex, 0, removed);
    internalRanking.value = newRanking;
    emits("update:ranking", newRanking);

    draggedIndex.value = newIndex;
    dragStartY.value = y;
  }
}

function stopDrag() {
  draggedIndex.value = null;
  document.removeEventListener("mousemove", handleMove);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleMove);
  document.removeEventListener("touchend", stopDrag);
  emits("update:ranking", internalRanking.value);
}

function getItemStyle(index) {
  if (index === draggedIndex.value) {
    const translateY = dragCurrentY.value - dragStartY.value;

    return {
      transform: `translateY(${translateY}px)`,
      zIndex: 1000,
      transition: "none",
    };
  }
}

function onDropdownChange(currentIndex, newIndex) {
  const newRanking = [...internalRanking.value];
  const [removed] = newRanking.splice(currentIndex, 1);
  newRanking.splice(newIndex - 1, 0, removed);
  internalRanking.value = newRanking;
  emits("update:ranking", newRanking);
}
</script>

<style scoped>
.formester-radio-options {
  position: relative;
}

.formester-control {
  position: relative;
  user-select: none;
  transition: all 0.2s ease-in-out;
}

.formester-control.is-dragging {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.drag-handle {
  cursor: move;
  display: flex;
  align-items: center;
  padding: 0 8px;
  touch-action: none;
}
</style>
